import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { LoadingSpinner } from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

export default function StaffAutoLogin() {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [loginCompleted, setLoginCompleted] = useState(false)
  const { staff } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // If staff is already logged in, redirect immediately
  useEffect(() => {
    if (staff) {
      console.log('Staff already logged in, redirecting to dashboard')
      navigate(staff.role === 'admin' ? '/admin' : '/staff/dashboard', { replace: true })
    }
  }, [staff, navigate])

  useEffect(() => {
    const performAutoLogin = async () => {
      // Prevent running multiple times
      if (loginCompleted) return
      
      try {
        const token = searchParams.get('token')
        
        if (!token) {
          setError('Missing authentication token')
          return
        }

        console.log('Starting auto-login with token:', token)

        // Call the auto-login endpoint
        const response = await fetch(
          `https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api/staff/auto-login?token=${encodeURIComponent(token)}`
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Auto-login failed')
        }

        const { staff: staffData, session: sessionData } = await response.json()
        console.log('Auto-login response:', { staffData, sessionData })

        // Store staff session in localStorage
        const sessionToStore = { 
          staff: staffData, 
          session: sessionData,
          timestamp: Date.now()
        }
        localStorage.setItem('staffSession', JSON.stringify(sessionToStore))
        console.log('Stored staff session in localStorage:', sessionToStore)
        
        toast.success(`Welcome back, ${staffData.name}!`)
        setLoginCompleted(true)
        
        // Navigate directly to dashboard without page reload
        const targetPath = staffData.role === 'admin' ? '/admin' : '/staff/dashboard'
        console.log('Navigating to:', targetPath)
        
        // Give a moment for localStorage to be written, then navigate
        setTimeout(() => {
          navigate(targetPath, { replace: true })
        }, 500)
        
      } catch (error: any) {
        console.error('Auto-login error:', error)
        setError(error.message || 'Auto-login failed')
        toast.error('Auto-login failed. Please try manual login.')
        
        // Redirect to manual login after a delay
        setTimeout(() => {
          navigate('/staff/login', { replace: true })
        }, 3000)
      } finally {
        setIsLoading(false)
      }
    }

    // Only run auto-login if not already logged in as staff and not completed
    if (!staff && !loginCompleted) {
      performAutoLogin()
    }
  }, [searchParams, navigate, staff, loginCompleted])

  if (staff) {
    // Already logged in, will redirect via useEffect
    return null
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-vapour-700">Authenticating staff access...</p>
          <p className="mt-2 text-sm text-vapour-500">Please wait while we log you in securely</p>
        </div>
      </div>
    )
  }

  if (loginCompleted) {
    return (
      <div className="min-h-screen bg-dark-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-vapour-700">Login successful! Redirecting...</p>
          <p className="mt-2 text-sm text-vapour-500">Taking you to the staff dashboard</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4">
            <h2 className="text-red-800 font-semibold mb-2">🔒 Authentication Failed</h2>
            <p className="text-red-700">{error}</p>
          </div>
          <p className="text-vapour-600 mb-4">Redirecting to manual login...</p>
          <LoadingSpinner size="sm" />
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Need help?</strong> Try the manual login with your staff credentials.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}